import { Container, Title, Text, Stack, Badge, Button, SegmentedControl, LoadingOverlay, Alert, Card, Group, Box, ScrollArea, Loader } from '@mantine/core';
import { IconCalendar, IconBook, IconAlertCircle, IconCards, IconChevronRight, IconArrowDown, IconRobot } from '@tabler/icons-react';
import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyAssignments } from '@/features/student/hooks/useAssignments';
import { useThemes } from '@/features/story/hooks/useStory';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Mobile Assignments Page
 *
 * Card-based assignment list optimized for mobile:
 * - Large touch targets
 * - Vertical card layout
 * - Clear visual hierarchy
 * - Simplified filter (pending/all)
 */
export function MobileAssignmentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const { data: assignments, isLoading, error, refetch } = useMyAssignments(filter === 'all');
  const { data: themes } = useThemes();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const touchStartY = useRef(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Create a map of theme ID to theme label for quick lookup
  const themeMap = useMemo(() => {
    if (!themes) return new Map();
    return new Map(themes.map(theme => [theme.id, theme.label]));
  }, [themes]);

  // Keep a ref to pullDistance for the native event handler
  const pullDistanceRef = useRef(pullDistance);
  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  // Pull-to-refresh using native event listeners for proper passive: false support
  useEffect(() => {
    const scrollAreaElement = scrollAreaRef.current;
    if (!scrollAreaElement) return;

    const handleTouchStart = (e: TouchEvent) => {
      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        touchStartY.current = e.touches[0].clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartY.current === 0) return;

      const viewport = scrollAreaElement.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement | null;
      const scrollTop = viewport?.scrollTop ?? 0;

      if (scrollTop <= 1) {
        const currentY = e.touches[0].clientY;
        const distance = Math.max(0, currentY - touchStartY.current);

        if (distance > 0) {
          e.preventDefault();
          setPullDistance(Math.min(distance, 80));
        }
      }
    };

    const handleTouchEnd = async () => {
      const currentPullDistance = pullDistanceRef.current;
      if (currentPullDistance > 60) {
        setIsRefreshing(true);
        await refetch();
        queryClient.invalidateQueries({ queryKey: ['assignments'] });
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 500);
      } else {
        setPullDistance(0);
      }
      touchStartY.current = 0;
    };

    scrollAreaElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollAreaElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    scrollAreaElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      scrollAreaElement.removeEventListener('touchstart', handleTouchStart);
      scrollAreaElement.removeEventListener('touchmove', handleTouchMove);
      scrollAreaElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [refetch, queryClient]);

  if (isLoading) {
    return (
      <Container size="sm" py="xl" pos="relative" mih={400}>
        <LoadingOverlay visible />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="sm" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error instanceof Error ? error.message : 'Failed to load assignments'}
        </Alert>
      </Container>
    );
  }

  const isOverdue = (dueDate: string | null) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getAssignmentDetails = (assignment: any) => {
    if (assignment.assignable_type === 'flashcard_deck') {
      return {
        icon: <IconCards size={20} />,
        type: 'Flashcards',
        detail: assignment.deck_title || 'Flashcard Deck',
        subDetail: assignment.deck_card_count ? `${assignment.deck_card_count} cards` : null,
        color: 'violet',
      };
    } else if (assignment.assignable_type === 'story_theme') {
      const themeName = themeMap.get(assignment.assignable_id) || assignment.story_theme || 'Story Theme';
      return {
        icon: <IconBook size={20} />,
        type: 'Reading Theme',
        detail: themeName,
        subDetail: assignment.completed_story_title ? `Read: ${assignment.completed_story_title}` : null,
        color: 'blue',
      };
    } else if (assignment.assignable_type === 'story') {
      return {
        icon: <IconBook size={20} />,
        type: 'Story',
        detail: assignment.story_title || 'Reading Story',
        subDetail: null,
        color: 'teal',
      };
    }
    return {
      icon: <IconBook size={20} />,
      type: 'Reading',
      detail: 'Story',
      subDetail: null,
      color: 'gray',
    };
  };

  const handleAssignmentClick = (assignment: any) => {
    if (assignment.assignable_type === 'flashcard_deck') {
      navigate(`/flashcards/review/${assignment.assignable_id}`, {
        state: { assignmentId: assignment.id }
      });
    } else if (assignment.assignable_type === 'story') {
      navigate(`/story/${assignment.assignable_id}`, {
        state: { assignmentId: assignment.id }
      });
    } else if (assignment.assignable_type === 'story_theme') {
      navigate(`/assignments/theme/${assignment.assignable_id}`, {
        state: { assignmentId: assignment.id }
      });
    } else {
      navigate('/library/story/create');
    }
  };

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
        <Stack gap="md">
          <Title order={2}>My Assignments</Title>
          <SegmentedControl
            fullWidth
            value={filter}
            onChange={(value) => setFilter(value as 'pending' | 'all')}
            data={[
              { label: 'Pending', value: 'pending' },
              { label: 'All', value: 'all' },
            ]}
          />
        </Stack>
      </Box>

      {/* Assignments List */}
      <ScrollArea
        ref={scrollAreaRef}
        style={{ flex: 1 }}
        offsetScrollbars
      >
        {/* Pull-to-refresh indicator */}
        <Box
          style={{
            height: `${pullDistance}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: pullDistance === 0 ? 'height 0.3s ease-out' : 'none',
            overflow: 'hidden',
          }}
        >
          {isRefreshing ? (
            <Loader size="sm" />
          ) : pullDistance > 0 && (
            <IconArrowDown
              size={20}
              style={{
                opacity: pullDistance / 80,
                transform: `rotate(${pullDistance > 60 ? 180 : 0}deg)`,
                transition: 'transform 0.2s',
              }}
            />
          )}
        </Box>

        <Container size="sm" py="md">
          {assignments && assignments.length > 0 ? (
          <Stack gap="md">
            {assignments.map((assignment: any) => {
              const details = getAssignmentDetails(assignment);
              const overdue = isOverdue(assignment.due_date);

              return (
                <Card
                  key={assignment.id}
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  onClick={() => handleAssignmentClick(assignment)}
                  style={{ cursor: 'pointer' }}
                >
                  <Stack gap="sm">
                    {/* Type Badge and Icon */}
                    <Group justify="space-between" wrap="nowrap">
                      <Group gap="xs">
                        <Box style={{ color: `var(--mantine-color-${details.color}-6)` }}>
                          {details.icon}
                        </Box>
                        <Badge variant="light" color={details.color} size="sm">
                          {details.type}
                        </Badge>
                        {assignment.assignment_source === 'system' && (
                          <Badge leftSection={<IconRobot size={12} />} variant="light" color="grape" size="xs">
                            Daily
                          </Badge>
                        )}
                      </Group>
                      <IconChevronRight size={20} color="var(--mantine-color-gray-6)" />
                    </Group>

                    {/* Title */}
                    <Box>
                      <Text fw={600} size="lg">
                        {assignment.title}
                      </Text>
                      <Text size="sm" c="dimmed" mt={4}>
                        {details.detail}
                      </Text>
                      {details.subDetail && (
                        <Text size="xs" c="dimmed" mt={2}>
                          {details.subDetail}
                        </Text>
                      )}
                    </Box>

                    {/* Description */}
                    {assignment.description && (
                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {assignment.description}
                      </Text>
                    )}

                    {/* Footer with Due Date and Status */}
                    <Group justify="space-between" mt="xs">
                      {assignment.due_date ? (
                        <Group gap="xs">
                          <IconCalendar size={14} color={overdue ? 'var(--mantine-color-red-6)' : 'var(--mantine-color-gray-6)'} />
                          <Text size="sm" c={overdue ? 'red' : 'dimmed'}>
                            {overdue ? 'Overdue: ' : 'Due: '}
                            {new Date(assignment.due_date).toLocaleDateString()}
                          </Text>
                        </Group>
                      ) : (
                        <Text size="sm" c="dimmed">No due date</Text>
                      )}

                      {assignment.status === 'completed' ? (
                        <Badge color="green" variant="light" size="sm">
                          Completed
                        </Badge>
                      ) : overdue ? (
                        <Badge color="red" variant="light" size="sm">
                          Overdue
                        </Badge>
                      ) : (
                        <Badge color="blue" variant="light" size="sm">
                          Pending
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        ) : (
          <Stack align="center" justify="center" py="xl" gap="md">
            <IconBook size={48} stroke={1.5} color="var(--mantine-color-gray-5)" />
            <Stack align="center" gap="xs">
              <Text size="lg" fw={500} c="dimmed">
                {filter === 'pending' ? 'No pending assignments' : 'No assignments yet'}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {filter === 'pending'
                  ? 'Great job! You\'re all caught up'
                  : 'Assignments from your teacher will appear here'}
              </Text>
            </Stack>
            {filter === 'pending' && (
              <Button
                variant="light"
                onClick={() => setFilter('all')}
                mt="md"
              >
                View All Assignments
              </Button>
            )}
          </Stack>
        )}
        </Container>
      </ScrollArea>
    </Box>
  );
}
